import * as actionTypes from "./types";
import base64url from "base64url";
import axios from "axios";
import axiosRetry from "axios-retry";

axiosRetry(axios, { retries: 3, retryCondition: (_error) => true });

const currentDomain = window.location.origin;

const REQUEST_QUEUE = [];
let ongoing_requests = 0;

const getApiUrl = (apiMode, campaignId, platform) => {
  const basePath =
    apiMode === "prod" ? currentDomain : "https://app-staging.speechifai.tech";
  return `${basePath}/api/v1/campaign/${campaignId}/ready_made_posts/${platform}`;
};

export const getMediaBaseUrl = () => {
  return window.IS_PRODUCTION_ENVIRONMENT
    ? "https://media.speechifai.tech"
    : "https://media-staging.speechifai.tech";
};

// Actions

export const updatePlatform = (platform) => (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_PLATFORM, payload: platform });
};

export const updateGooglePlatform = (platform) => (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_GOOGLE_PLATFORM, payload: platform });
};

export const updateMode = (mode) => (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_MODE, payload: mode });
};

export const setCategory = (category) => (dispatch) => {
  dispatch({ type: actionTypes.SET_CATEGORY, payload: category });
};

export const setLinkVParam = (url) => (dispatch) => {
  dispatch({ type: actionTypes.SET_LINK_V_PARAM, payload: url });
};

export const setLinkUrlParam = (url) => (dispatch) => {
  dispatch({ type: actionTypes.SET_LINK_URL_PARAM, payload: url });
};

export const setWebsiteEmbedding = (bool) => (dispatch) => {
  dispatch({ type: actionTypes.SET_WEBSITE_EMBEDDING, payload: bool });
};

export const setMultirow = (bool) => (dispatch) => {
  dispatch({ type: actionTypes.SET_MULTIROW, payload: bool });
};

export const setCategoryID = (id) => (dispatch) => {
  dispatch({ type: actionTypes.SET_CATEGORYID, payload: id });
};

export const setCategories = (categories) => (dispatch) => {
  dispatch({ type: actionTypes.SET_CATEGORIES, payload: categories });
  return Promise.resolve(categories);
};

export const setUrlParams = (params) => (dispatch) => {
  dispatch({ type: actionTypes.SET_URL_PARAMS, payload: params });
};

export const filterPosts = (id) => (dispatch) => {
  if (id === 0) {
    dispatch({ type: actionTypes.RETURN_POSTS });
  } else {
    dispatch({ type: actionTypes.FILTER_POSTS, payload: id });
  }
};

export const setLanding = (campaignId, apiMode) => (dispatch) => {
  dispatch({ type: actionTypes.LANDING_START });
  return axios
    .get(
      apiMode === "prod"
        ? `${currentDomain}/api/v1/campaign/${campaignId}`
        : `https://app-staging.speechifai.tech/api/v1/campaign/${campaignId}`
    )
    .then((res) => {
      dispatch({ type: actionTypes.LANDING_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({ type: actionTypes.LANDING_FAILURE, payload: err.message });
    });
};

export const setReadyMade =
  (campaignId, platform, apiMode) => async (dispatch) => {
    dispatch({ type: actionTypes.READY_MADE_START });

    try {
      const res = await axios.get(getApiUrl(apiMode, campaignId, platform));
      dispatch({ type: actionTypes.READY_MADE_SUCCESS, payload: res.data });

      const posts = res.data.readyMadePosts;
      const resolvedPosts = await Promise.allSettled(
        posts.map(async (post) => {
          if (post.imageContext && post.imageContext.image.externalMediaID) {
            try {
              const resolvedMedia = await resolveNewMediaServiceIDs(
                post.imageContext.image.externalMediaID
              );
              post.readyMadePost.resolvedMedia = resolvedMedia;
              return post;
            } catch (error) {
              console.error(error);
              return post;
            }
          } else {
            return post;
          }
        })
      );

      const resolvedData = {
        ...res.data,
        readyMadePosts: resolvedPosts.map((postResult) => postResult.value),
      };

      return Promise.resolve(resolvedData);
    } catch (err) {
      dispatch({ type: actionTypes.READY_MADE_FAILURE, payload: err.message });
      return Promise.reject(err.message);
    }
  };

export const resolveNewMediaServiceIDs = (externalMediaID) => {
  return new Promise((resolve, reject) => {
    REQUEST_QUEUE.push({
      type: "media",
      id: externalMediaID,
      successCallback: resolve,
      errorCallback: reject,
    });
    evaluate_queue();
  });
};

async function evaluate_queue() {
  if (ongoing_requests > 10) {
    return;
  }

  if (REQUEST_QUEUE.length === 0) {
    return;
  }

  ongoing_requests++;
  const request_data = REQUEST_QUEUE.shift() ?? {
    type: "",
    id: "",
    successCallback: () => {},
    errorCallback: () => {},
  };

  try {
    let responseData;

    if (request_data.type === "media") {
      const baseURL = getMediaBaseUrl();
      const url = `/api/v1/media/${request_data.id}`;
      const res = await axios.get(`${baseURL}${url}`, {
        raxConfig: {
          retry: 3,
          noResponseRetries: 3,
          retryDelay: 100,
        },
      });

      responseData = res.data;
    }

    request_data.successCallback(responseData);
  } catch (err) {
    request_data.errorCallback(err);
  } finally {
    ongoing_requests--;
    evaluate_queue();
  }
}

export const shortenUrl = (campaign, apiMode) => async (dispatch) => {
  dispatch({ type: actionTypes.URL_SHORTEN_START });
  const encodedURL = base64url(campaign.url);

  const apiDomain =
    apiMode === "prod" ? currentDomain : "https://app-staging.speechifai.tech";
  let staticLink = `${apiDomain}/campaign/${campaign.campaignID}/static?url=${encodedURL}&image_id=${campaign.imageID}&social_network=${campaign.socialNetwork}`;

  if (campaign.linkCaption && campaign.linkCaption !== "") {
    staticLink += `&link_caption=${base64url(campaign.linkCaption)}`;
  }

  try {
    const res = await axios.post(`${apiDomain}/api/shortner_v1/shorten_url`, {
      campaignID: campaign.campaignID,
      url: staticLink,
    });

    dispatch({
      type: actionTypes.URL_SHORTEN_SUCCESS,
      payload: res.data.hash,
    });
    return `${apiDomain}/s/${res.data.hash}`;
  } catch (err) {
    dispatch({
      type: actionTypes.URL_SHORTEN_FAILURE,
      payload: err.message,
    });
    throw err.message;
  }
};

const writingAssistantURL = `https://api.ubiops.com/v2.1/projects/sosha-writing-assistant/deployments/sosha-ai-${window.IS_PRODUCTION_ENVIRONMENT ? "production" : "staging"}/requests`;
const ubiOpsToken = window.IS_PRODUCTION_ENVIRONMENT ? "7f8ca009cd73b2fe3d35ef93fe99f40372d64265" : "8b2e8424eaf148d15cc0d0440fb27f0e4e54dc3e";

export async function aiPostVariation(text, socialNetwork, campaignId) {
    const params = { "text": text, "social_network": socialNetwork, "tones": []}; //, "api": "unfiltered_ai"

    const new_text = await axios({
      method: "post",
      url: writingAssistantURL,
      data: {method: "create-variation-fine-tuned", data: params },
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Token ${ubiOpsToken}`,
      },
      raxConfig: {
        retry: 1,
        noResponseRetries: 1,
        retryDelay: 500,
      }}).then((res) => {
        const status = res.status;
      

        if(status === 200) {
          const data = res.data
          if(data.result.status === 200) {
            return Promise.resolve(data.result.data.text);
          } else {
            return Promise.resolve("");
          }
        } else {
          return Promise.resolve("");
        }
      })
    .catch((err) => {
      return Promise.reject(err.message);
    });

    return new_text;
};

export function displayAiForSharers(text) {
  if(text.includes("[AI-BEGIN]") && text.includes("[AI-END]")) {
    return true;
  } else {
    return false;
  }
}

export const trackPostShare = (campaign, apiMode) => async (dispatch) => {
  dispatch({ type: actionTypes.TRACK_SHARE });

  const apiUrl =
    apiMode === "prod"
      ? `${currentDomain}/api/analytics/register_share`
      : `https://app-staging.speechifai.tech/api/analytics/register_share`;

  try {
    const res = await axios.post(apiUrl, {
      campaignID: campaign.campaignID,
      sharedText: campaign.sharedText,
      sharedURL: campaign.shared_url,
      sharedPlatform: campaign.sharedPlatform,
      sharedImageURL: campaign.sharedImageURL,
      postType: campaign.postType,
      embed: campaign.embed,
      email: campaign.email,
    });
    console.log(res);

    if(window.gleamPush) {
      window.gleamPush();
    }
  } catch (err) {
    console.log(err.message, "MESSAGE");
    console.log(err.status, "STATUS");
  }
};

export const trackSession = (campaign, apiMode) => async (dispatch) => {
  dispatch({ type: actionTypes.TRACK_SESSION });

  const apiUrl =
    apiMode === "prod"
      ? `${currentDomain}/api/analytics/register_session`
      : `https://app-staging.speechifai.tech/api/analytics/register_session`;

  try {
    const res = await axios.post(apiUrl, {
      campaignID: campaign.campaignID,
      wp: campaign.wp,
    });
  } catch (err) {
    console.log(err.message, "MESSAGE");
    console.log(err.status, "STATUS");
  }
};

export const filterCategoriesByID = (id) => (dispatch) => {
  dispatch({ type: actionTypes.FILTER_CATEGORY, payload: id });
};

export const setDefaultCategory = (category, categories) => (dispatch) => {
  categories.unshift(category);
  dispatch({ type: actionTypes.SET_DEFAULT_CATEGORY, payload: categories });
  return Promise.resolve(categories);
};

export const gtagPostShare =
  ({
    campaign_id,
    campaign_name,
    shared_text,
    shared_url,
    shared_platform,
    shared_image_url,
    post_type,
  }) =>
  (dispatch) => {
    dispatch({ type: actionTypes.GTAG_POST_SHARE });
    window.gtag("event", "share", {
      campaign_id: campaign_id,
      campaign_name: campaign_name,
      shared_text: shared_text,
      shared_url: shared_url,
      shared_platform: shared_platform,
      shared_image_url: shared_image_url,
      post_type: post_type,
    });
  };

export const setEditing = (editState, id) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_EDITING,
    payload: { editState, id },
  });
};

export const editPostText = (postID, text) => (dispatch) => {
  dispatch({
    type: actionTypes.EDIT_POST_TEXT,
    payload: { id: postID, text: text },
  });
};

export const setCustomtext = (bool) => (dispatch) => {
  dispatch({ type: actionTypes.SET_CUSTOM_TEXT, payload: bool });
};
